import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, AUTH_USER_KEY } from '../utils/api';
import { API_BASE_URL } from '../config';
import { Site } from '../types/interfaces';
import { Role } from '../types/enums';
import TtbForm51309Sheet from './TtbForm51309Sheet';
import { centralToday } from '../utils/centralDate';

type Cols = {
  cellar: number;
  rackingBulk: number;
  rackingKeg: number;
  bottlingBulk: number;
  bottlingCase: number;
  totals: number;
};

type SourceRow = {
  id: string | null;
  date: string | null;
  label: string;
  barrels: number;
  column?: string | null;
};

type ReportLine = {
  line: number;
  description: string;
  computed?: boolean;
  cols: Cols;
  cellar: number;
  rackingBulk: number;
  rackingKeg: number;
  bottlingBulk: number;
  bottlingCase: number;
  totals: number;
  sources: SourceRow[];
};

type ComparisonDiff = {
  line: number;
  description: string;
  tilly: Cols;
  ekos: Cols;
  columns: { column: string; tilly: number; ekos: number; delta: number }[];
  sources: SourceRow[];
};

type BrewersReport = {
  form: string;
  formRevision: string;
  omb: string;
  title: string;
  readOnly: boolean;
  filingNote: string;
  site: { siteId: string; name: string; type?: string };
  header: {
    breweryName: string;
    ein: string;
    ttbNumber: string;
    street: string;
    city: string;
    county: string;
    state: string;
    zip: string;
    phone: string;
  };
  period: {
    year: number;
    quarter: number;
    label: string;
    startDate: string;
    endDate: string;
    beginningAsOf: string;
    reportKind: string;
  };
  lines: ReportLine[];
  balanceCheck: {
    agrees: boolean;
    endingMatchesActivity: boolean;
    onHand?: {
      asOfDate: string;
      rackingKeg: number;
      bottlingCase: number;
      line33RackingKeg: number;
      line33BottlingCase: number;
      matches: boolean;
    };
  };
  reviewNotes?: { code: string; line: number; message: string }[];
  comparison?: {
    available: boolean;
    reason?: string;
    source?: string;
    matchCount?: number;
    diffCount?: number;
    diffs?: ComparisonDiff[];
  };
  warnings?: {
    code: string;
    line: number;
    message: string;
    calculated?: number;
    shown?: number;
  }[];
  quarterStatus?: 'open' | 'closed';
  archive?: {
    immutable: boolean;
    closedAt: string | null;
    closedBy: string | null;
    source: string | null;
  } | null;
  remarks?: {
    entries?: {
      line: number;
      date: string | null;
      item: string;
      barrels: number;
      reason: string;
      needsReason?: boolean;
      text: string;
    }[];
    generatedText?: string;
    text?: string;
    edited?: boolean;
    needsReasonCount?: number;
  } | null;
};

type ClosedQuarter = {
  siteId: string;
  year: number;
  quarter: number;
  closedAt: string | null;
};

const SAVE_CLOSING_PHRASE = 'SAVE_TTB_CLOSING';

function isReportAdmin(): boolean {
  try {
    const role = JSON.parse(localStorage.getItem(AUTH_USER_KEY) || '{}').role;
    return role === Role.SuperAdmin || role === Role.Admin;
  } catch {
    return false;
  }
}

const EARLIEST = { year: 2026, quarter: 3 };

function quarterOptions(): { year: number; quarter: number; label: string }[] {
  // Quarter of today's America/Chicago date (TTB quarters are brewery-local).
  const [currentYear, currentMonth] = centralToday().split('-').map(Number);
  const currentQuarter = Math.floor((currentMonth - 1) / 3) + 1;
  const out: { year: number; quarter: number; label: string }[] = [];
  const labels = ['January - March', 'April - June', 'July - September', 'October - December'];
  for (let y = EARLIEST.year; y <= currentYear; y += 1) {
    for (let q = 1; q <= 4; q += 1) {
      if (y === EARLIEST.year && q < EARLIEST.quarter) continue;
      if (y === currentYear && q > currentQuarter) continue;
      out.push({ year: y, quarter: q, label: `Q${q} ${y} (${labels[q - 1]})` });
    }
  }
  return out;
}

function fmtBbl(n: number): string {
  return Number(n || 0).toFixed(2);
}

const COL_KEYS: (keyof Cols)[] = [
  'cellar',
  'rackingBulk',
  'rackingKeg',
  'bottlingBulk',
  'bottlingCase',
  'totals',
];

const TtbBrewersReport: React.FC = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [siteId, setSiteId] = useState('');
  const quarters = useMemo(() => quarterOptions(), []);
  const [periodKey, setPeriodKey] = useState(() => {
    const opts = quarterOptions();
    const q3 = opts.find((o) => o.year === 2026 && o.quarter === 3);
    return q3 ? `${q3.year}-${q3.quarter}` : opts[0] ? `${opts[0].year}-${opts[0].quarter}` : '2026-3';
  });
  const [report, setReport] = useState<BrewersReport | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [remarksDraft, setRemarksDraft] = useState('');
  const [remarksSaving, setRemarksSaving] = useState(false);
  const [closedKeys, setClosedKeys] = useState<Set<string>>(new Set());
  const canCloseQuarter = isReportAdmin();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/sites`);
        if (!res.ok) throw new Error(`Failed to load sites (${res.status})`);
        const data = (await res.json()) as Site[];
        if (cancelled) return;
        const breweries = (data || []).filter(
          (s) =>
            (s.siteId === 'BR-AL-20019' || s.siteId === 'BR-AL-20088') && s.enabled !== 0
        );
        setSites(breweries);
        if (!siteId && breweries.length) {
          const madison = breweries.find((s) => s.siteId === 'BR-AL-20019');
          setSiteId((madison || breweries[0]).siteId);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load sites');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  useEffect(() => {
    if (!siteId) {
      setClosedKeys(new Set());
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(
          `${API_BASE_URL}/api/reports/ttb-5130-9/archives?siteId=${encodeURIComponent(siteId)}`
        );
        if (!res.ok) return;
        const body = (await res.json()) as { archives?: ClosedQuarter[] };
        if (cancelled) return;
        setClosedKeys(
          new Set((body.archives || []).map((row) => `${row.year}-${row.quarter}`))
        );
      } catch {
        if (!cancelled) setClosedKeys(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const selectedPeriod = useMemo(() => {
    const [y, q] = periodKey.split('-').map(Number);
    return { year: y, quarter: q };
  }, [periodKey]);

  const reportUrl = useCallback(
    (format?: string) => {
      const qs = new URLSearchParams({
        siteId,
        year: String(selectedPeriod.year),
        quarter: String(selectedPeriod.quarter),
      });
      if (format) qs.set('format', format);
      return `${API_BASE_URL}/api/reports/ttb-5130-9?${qs}`;
    },
    [siteId, selectedPeriod.year, selectedPeriod.quarter]
  );

  const generate = useCallback(async () => {
    if (!siteId) {
      setError('Choose a brewery site');
      return;
    }
    setLoading(true);
    setError(null);
    setSaveMessage(null);
    setExpanded(null);
    try {
      const res = await apiFetch(reportUrl());
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `Report failed (${res.status})`);
      }
      const next = body as BrewersReport;
      setReport(next);
      setRemarksDraft(next.remarks?.text || next.remarks?.generatedText || '');
    } catch (err: unknown) {
      setReport(null);
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  }, [siteId, reportUrl]);

  const saveRemarks = async () => {
    if (!siteId || !report || report.quarterStatus === 'closed') return;
    setRemarksSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/reports/ttb-5130-9/remarks`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          year: selectedPeriod.year,
          quarter: selectedPeriod.quarter,
          remarksText: remarksDraft,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Save remarks failed (${res.status})`);
      setReport((prev) =>
        prev
          ? {
              ...prev,
              remarks: {
                ...(prev.remarks || {}),
                text: remarksDraft,
                edited: true,
              },
            }
          : prev
      );
      setSaveMessage('Part 3 Remarks saved for this quarter.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save remarks');
    } finally {
      setRemarksSaving(false);
    }
  };

  const closeQuarter = async () => {
    if (!siteId || !report || report.quarterStatus === 'closed') return;
    const warn = (report.warnings || []).map((w) => w.message).join(' ');
    const needs = report.remarks?.needsReasonCount || 0;
    const ok = window.confirm(
      `Close Q${selectedPeriod.quarter} ${selectedPeriod.year} for ${siteId}? This stores an immutable archive of the full Brewer's Report (including Part 3 Remarks). Line 33 becomes the opening balance for the next quarter. A closed quarter cannot be overwritten.${
        needs ? `\n\n${needs} remark line(s) still say "reason needed".` : ''
      }${warn ? `\n\n${warn}` : ''}`
    );
    if (!ok) return;
    setSaving(true);
    setError(null);
    setSaveMessage(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/reports/ttb-5130-9/closing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-confirm-phrase': SAVE_CLOSING_PHRASE,
        },
        body: JSON.stringify({
          siteId,
          year: selectedPeriod.year,
          quarter: selectedPeriod.quarter,
          confirmPhrase: SAVE_CLOSING_PHRASE,
          remarksText: remarksDraft,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Close failed (${res.status})`);
      if (body.report) setReport(body.report as BrewersReport);
      setClosedKeys((prev) => {
        const next = new Set(prev);
        next.add(`${selectedPeriod.year}-${selectedPeriod.quarter}`);
        return next;
      });
      setSaveMessage(
        `Q${selectedPeriod.quarter} ${selectedPeriod.year} is closed. Print and PDF use the archived report.`
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to close quarter');
    } finally {
      setSaving(false);
    }
  };

  const download = (format: 'csv' | 'csv-sources' | 'form' | 'pdf') => {
    if (!siteId) return;
    (async () => {
      const res = await apiFetch(reportUrl(format));
      if (!res.ok) {
        setError(`Export failed (${res.status})`);
        return;
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const ext =
        format === 'form' ? 'txt' : format === 'pdf' ? 'pdf' : format === 'csv-sources' ? 'sources.csv' : 'csv';
      a.download = `ttb-5130-9-${siteId}-Q${selectedPeriod.quarter}-${selectedPeriod.year}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    })();
  };

  /** Portrait legal F 5130.9 (same page size as Ekos / pay.gov). Also used for Ctrl+P. */
  const prepareTtbPrint = useCallback(() => {
    document.body.classList.remove('bol-printing');
    document.body.classList.add('ttb-brop-printing');
    let pageStyle = document.getElementById('ttb-print-page-style') as HTMLStyleElement | null;
    if (!pageStyle) {
      pageStyle = document.createElement('style');
      pageStyle.id = 'ttb-print-page-style';
      document.head.appendChild(pageStyle);
    }
    pageStyle.textContent = '@media print { @page { size: 8.5in 14in; margin: 0; } }';
  }, []);

  const cleanupTtbPrint = useCallback(() => {
    document.body.classList.remove('ttb-brop-printing');
    document.getElementById('ttb-print-page-style')?.remove();
  }, []);

  useEffect(() => {
    if (!report) return undefined;
    const onBefore = () => prepareTtbPrint();
    const onAfter = () => cleanupTtbPrint();
    window.addEventListener('beforeprint', onBefore);
    window.addEventListener('afterprint', onAfter);
    return () => {
      window.removeEventListener('beforeprint', onBefore);
      window.removeEventListener('afterprint', onAfter);
      cleanupTtbPrint();
    };
  }, [report, prepareTtbPrint, cleanupTtbPrint]);

  const printForm = () => {
    prepareTtbPrint();
    window.print();
  };

  return (
    <section className="ttb-report" aria-labelledby="ttb-5130-9-heading">
      <p className="no-print">
        <Link to="/reporting">All reports</Link>
      </p>
      <h3 id="ttb-5130-9-heading" className="ttb-report__title no-print">
        TTB F 5130.9 — Brewer&apos;s Report of Operations
      </h3>
      <p className="ttb-report__note no-print">
        Generates the F 5130.9 Part 1 Beer Summary (filed quarterly) from Tilly data — same layout
        Todd files on pay.gov. Tilly does not file with TTB. Opening a quarter does not close it.
        Close quarter stores an immutable copy for that brewery. Madison and Athens close
        separately. Q3 2026 is compared automatically to Todd&apos;s Ekos-filed sample BROP.
      </p>

      <div className="ttb-report__controls no-print">
        <label>
          Brewery site
          <select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value="">Select site…</option>
            {sites.map((s) => (
              <option key={s.siteId} value={s.siteId}>
                {s.name} ({s.siteId})
              </option>
            ))}
          </select>
        </label>
        <label>
          Quarter
          <select value={periodKey} onChange={(e) => setPeriodKey(e.target.value)}>
            {quarters.map((q) => (
              <option key={`${q.year}-${q.quarter}`} value={`${q.year}-${q.quarter}`}>
                {q.label}
                {closedKeys.has(`${q.year}-${q.quarter}`) ? ' — Closed' : ''}
              </option>
            ))}
          </select>
        </label>
        <button type="button" onClick={generate} disabled={loading || !siteId}>
          {loading ? 'Generating…' : 'Generate report'}
        </button>
      </div>

      {error && <p className="ttb-report__error">{error}</p>}

      {report && (
        <div className="ttb-report__output">
          {report.quarterStatus === 'closed' ? (
            <p className="ttb-report__status ttb-report__status--closed no-print" data-testid="ttb-quarter-closed">
              Closed archive
              {report.archive?.closedAt ? ` · ${report.archive.closedAt.slice(0, 10)}` : ''}
              {' '}
              — print and PDF use this snapshot
            </p>
          ) : (
            <p className="ttb-report__status ttb-report__status--open no-print" data-testid="ttb-quarter-open">
              Open — live report. Nothing is locked until you close the quarter.
            </p>
          )}
          <div className="ttb-report__actions no-print">
            <button type="button" onClick={() => download('pdf')}>
              Download PDF
            </button>
            <button type="button" onClick={() => download('form')}>
              Export form (TXT)
            </button>
            <button type="button" onClick={() => download('csv')}>
              Export CSV
            </button>
            <button type="button" onClick={() => download('csv-sources')}>
              Export sources CSV
            </button>
            <button type="button" onClick={printForm}>
              Print
            </button>
            {canCloseQuarter && report.quarterStatus !== 'closed' && (
              <button type="button" onClick={closeQuarter} disabled={saving} data-testid="ttb-close-quarter">
                {saving ? 'Closing quarter…' : 'Close quarter'}
              </button>
            )}
          </div>
          {saveMessage && <p className="ttb-report__note no-print">{saveMessage}</p>}
          {(report.warnings || []).map((w, i) => (
            <p key={`${w.code}-${w.line}-${i}`} className="ttb-report__warning" role="alert">
              {w.message}
            </p>
          ))}
          {(report.reviewNotes || []).map((n, i) => (
            <p key={`${n.code}-${n.line}-${i}`} className="ttb-report__note no-print" data-testid="ttb-review-note">
              {n.message}
            </p>
          ))}

          <section className="ttb-report__remarks no-print" data-testid="ttb-remarks-editor">
            <h4>Part 3 – Remarks</h4>
            <p className="ttb-report__note">
              Auto-built from lines 11 (gains), 28 (destroyed), 30 (losses), and 31 (shortages).
              Edit before printing; text is stored with the quarter close.
              {(report.remarks?.needsReasonCount || 0) > 0
                ? ` ${report.remarks?.needsReasonCount} line(s) still need a reason.`
                : ''}
            </p>
            <textarea
              value={remarksDraft}
              onChange={(e) => setRemarksDraft(e.target.value)}
              rows={8}
              style={{ width: '100%', fontFamily: 'monospace', fontSize: 13 }}
              readOnly={report.quarterStatus === 'closed'}
              data-testid="ttb-remarks-text"
            />
            {canCloseQuarter && report.quarterStatus !== 'closed' && (
              <button type="button" onClick={saveRemarks} disabled={remarksSaving} data-testid="ttb-remarks-save">
                {remarksSaving ? 'Saving remarks…' : 'Save remarks'}
              </button>
            )}
          </section>

          <div className="ttb-report__screen">
          <header className="ttb-report__form-header">
            <div className="ttb-report__form-title">
              <div>DEPARTMENT OF THE TREASURY</div>
              <div>ALCOHOL AND TOBACCO TAX AND TRADE BUREAU (TTB)</div>
              <div className="ttb-report__form-name">BREWER&apos;S REPORT OF OPERATIONS</div>
              <div>
                {report.form} ({report.formRevision}) · OMB No. {report.omb}
              </div>
            </div>
            <div className="ttb-report__form-meta">
              <div>
                <span>EIN</span> {report.header.ein}
              </div>
              <div>
                <span>TTB No.</span> {report.header.ttbNumber}
              </div>
              <div>
                <span>Phone</span> {report.header.phone}
              </div>
            </div>
            <div className="ttb-report__form-identity">
              <div>
                <strong>{report.header.breweryName}</strong>
              </div>
              <div>
                {report.header.street}, {report.header.city}, {report.header.state}{' '}
                {report.header.zip}
              </div>
              <div>
                Quarterly report for {report.period.label} {report.period.year} (
                {report.period.startDate} – {report.period.endDate})
              </div>
            </div>
            <div className="ttb-report__filing no-print">{report.filingNote}</div>
          </header>

          <div className="ttb-report__table-wrap">
            <table className="ttb-report__table ttb-report__table--brop">
              <thead>
                <tr>
                  <th rowSpan={2} scope="col">
                    Line
                  </th>
                  <th rowSpan={2} scope="col">
                    (a) Operations
                  </th>
                  <th rowSpan={2} scope="col" className="ttb-report__num">
                    (b) Cellar
                  </th>
                  <th colSpan={2} scope="colgroup">
                    Racking
                  </th>
                  <th colSpan={2} scope="colgroup">
                    Bottling
                  </th>
                  <th rowSpan={2} scope="col" className="ttb-report__num">
                    (g) Totals
                  </th>
                </tr>
                <tr>
                  <th scope="col" className="ttb-report__num">
                    (c) Bulk
                  </th>
                  <th scope="col" className="ttb-report__num">
                    (d) Keg
                  </th>
                  <th scope="col" className="ttb-report__num">
                    (e) Bulk
                  </th>
                  <th scope="col" className="ttb-report__num">
                    (f) Case
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.lines.map((line) => {
                  const isOpen = expanded === line.line;
                  const hasSources = (line.sources || []).length > 0;
                  return (
                    <React.Fragment key={line.line}>
                      <tr className={line.computed ? 'ttb-report__computed' : undefined}>
                        <td>{line.line}</td>
                        <td>
                          {line.description}
                          {hasSources && (
                            <button
                              type="button"
                              className="ttb-report__drill no-print"
                              onClick={() => setExpanded(isOpen ? null : line.line)}
                            >
                              {isOpen ? 'Hide sources' : `Sources (${line.sources.length})`}
                            </button>
                          )}
                        </td>
                        {COL_KEYS.map((k) => (
                          <td key={k} className="ttb-report__num">
                            {fmtBbl(line[k])}
                          </td>
                        ))}
                      </tr>
                      {isOpen && hasSources && (
                        <tr className="ttb-report__sources-row no-print">
                          <td colSpan={8}>
                            <ul className="ttb-report__sources">
                              {line.sources.map((src, i) => (
                                <li key={`${src.id || 'x'}-${i}`}>
                                  <span className="ttb-report__src-date">{src.date || '—'}</span>
                                  <span className="ttb-report__src-label">
                                    {src.column ? `[${src.column}] ` : ''}
                                    {src.label}
                                  </span>
                                  <span className="ttb-report__num">{fmtBbl(src.barrels)}</span>
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </div>

          <TtbForm51309Sheet
            report={{
              ...report,
              remarks: { text: remarksDraft || report.remarks?.text || report.remarks?.generatedText || '' },
            }}
          />

          <div className="ttb-report__balance no-print">
            Balance: line 13 {report.balanceCheck.agrees ? 'agrees' : 'does not agree'} with line
            34
            {report.balanceCheck.endingMatchesActivity
              ? ''
              : '; ending inventory (line 33) differs from activity-derived ending — review sources'}
            .
            {report.balanceCheck.onHand && (
              <span data-testid="ttb-on-hand-check">
                {' '}
                Line 33 kegs/cases {report.balanceCheck.onHand.matches ? 'match' : 'do NOT match'} Tilly inventory on{' '}
                {report.balanceCheck.onHand.asOfDate} ({report.balanceCheck.onHand.line33RackingKeg.toFixed(2)} /{' '}
                {report.balanceCheck.onHand.rackingKeg.toFixed(2)} bbl kegs,{' '}
                {report.balanceCheck.onHand.line33BottlingCase.toFixed(2)} /{' '}
                {report.balanceCheck.onHand.bottlingCase.toFixed(2)} bbl cases).
              </span>
            )}
          </div>

          {report.comparison?.available && (
            <section
              className="ttb-report__compare no-print"
              aria-label="Ekos sample comparison"
            >
              <h4>Q3 2026 vs Ekos sample (Todd filing)</h4>
              <p>
                {report.comparison.diffCount === 0
                  ? `All ${report.comparison.matchCount} compared lines match the Ekos sample.`
                  : `${report.comparison.diffCount} line(s) differ from the Ekos sample; ${report.comparison.matchCount} match.`}
              </p>
              {(report.comparison.diffs || []).length > 0 && (
                <div className="ttb-report__table-wrap">
                  <table className="ttb-report__table ttb-report__table--brop">
                    <thead>
                      <tr>
                        <th>Line</th>
                        <th>Description</th>
                        <th>Column</th>
                        <th className="ttb-report__num">Tilly</th>
                        <th className="ttb-report__num">Ekos</th>
                        <th className="ttb-report__num">Δ</th>
                        <th>Tilly sources</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report.comparison.diffs || []).flatMap((d) =>
                        d.columns.map((c, i) => (
                          <tr key={`${d.line}-${c.column}`}>
                            {i === 0 ? (
                              <>
                                <td rowSpan={d.columns.length}>{d.line}</td>
                                <td rowSpan={d.columns.length}>{d.description}</td>
                              </>
                            ) : null}
                            <td>{c.column}</td>
                            <td className="ttb-report__num">{fmtBbl(c.tilly)}</td>
                            <td className="ttb-report__num">{fmtBbl(c.ekos)}</td>
                            <td className="ttb-report__num">{fmtBbl(c.delta)}</td>
                            {i === 0 ? (
                              <td rowSpan={d.columns.length}>
                                {(d.sources || []).length === 0 ? (
                                  <em>No Tilly source records</em>
                                ) : (
                                  <ul className="ttb-report__sources ttb-report__sources--compact">
                                    {d.sources.map((src, si) => (
                                      <li key={`${src.id || 's'}-${si}`}>
                                        <span className="ttb-report__src-date">{src.date || '—'}</span>
                                        <span className="ttb-report__src-label">
                                          {src.column ? `[${src.column}] ` : ''}
                                          {src.label}
                                        </span>
                                        <span className="ttb-report__num">{fmtBbl(src.barrels)}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </td>
                            ) : null}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </section>
  );
};

export default TtbBrewersReport;
